/**
 * GTA GSM Track Package - Main TypeScript Entry Point
 *
 * This file serves as the main entry point for the tracking functionality.
 * It initializes all components and handles the application lifecycle.
 */

import './styles/main.css';
import './styles/gta-swiper-gallery.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { TrackingApp } from '@/components/TrackingApp';
import { ApiService } from '@/services/ApiService';
import { EventEmitter } from '@/utils/EventEmitter';
import { ModalManager } from '@/utils/ModalManager';
import { TranslationHelper } from '@/utils/TranslationHelper';
import type { TrackingConfig } from '@/types';

// Swiper instance interface for proper typing
interface SwiperInstance {
  destroy(deleteInstance?: boolean, cleanStyles?: boolean): void;
  slides: HTMLElement[];
}

/**
 * Global application state
 */
class GtaGsmTrackingApp {
  private static instance: GtaGsmTrackingApp;
  private apiService: ApiService;
  private eventEmitter: EventEmitter;
  private modalManager: ModalManager;
  private isInitialized = false;
  private config: TrackingConfig;

  private constructor() {
    this.eventEmitter = new EventEmitter();
    this.config = this.getConfigFromLocalized();
    this.apiService = new ApiService(this.config.api);
    this.modalManager = ModalManager.getInstance();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): GtaGsmTrackingApp {
    if (!GtaGsmTrackingApp.instance) {
      GtaGsmTrackingApp.instance = new GtaGsmTrackingApp();
    }
    return GtaGsmTrackingApp.instance;
  }

  /**
   * Initialize the application
   */
  public async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Initializing tracking application...

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        await new Promise(resolve => {
          document.addEventListener('DOMContentLoaded', resolve);
        });
      }

      // Libraries are loaded via ES modules - no need to wait for globals

      // Initialize tracking forms
      this.initializeTrackingForms();

      // Initialize event listeners
      this.initializeEventListeners();

      // Initialize Flowbite components
      this.initializeFlowbite();

      // Initialize Swiper galleries
      this.initializeSwiper();

      // Initialize global modal event delegation
      this.initializeModals();

      this.isInitialized = true;
      // Application initialized successfully
    } catch (error) {
      console.error('[GTA GSM] Failed to initialize application:', error);
      this.showError('Failed to initialize tracking application');
    }
  }

  /**
   * Get configuration from WordPress localized script
   */
  private getConfigFromLocalized(): TrackingConfig {
    // Get config from WordPress wp_localize_script
    const globalConfig = window.gtaGsmConfig;

    // Check for queryto URL parameter to determine API endpoint
    const urlParams = new URLSearchParams(window.location.search);
    const queryTo = urlParams.get('queryto');
    
    let apiUrl = globalConfig?.api?.url || 'https://tms-api.gtagsm.com/trackings/track-my-package';
    
    // Override API URL based on queryto parameter
    if (queryTo === 'local') {
      apiUrl = 'http://localhost:5067/trackings/track-my-package';
    } else if (queryTo === 'qa') {
      apiUrl = 'https://tms-api-qa.gtagsm.com/trackings/track-my-package';
    }
    // Otherwise use default URL (production)

    return {
      api: {
        url: apiUrl,
        key: globalConfig?.api?.key || '',
        accountKey: globalConfig?.api?.accountKey || 'Portal',
        maxTrackingNumbers: globalConfig?.api?.maxTrackingNumbers || 30,
        timeout: globalConfig?.api?.timeout || 30000,
      },
      ui: {
        animationDuration: globalConfig?.ui?.animationDuration || 300,
      },
    };
  }

  /**
   * Initialize tracking forms on the page
   */
  private initializeTrackingForms(): void {
    const trackingContainers = document.querySelectorAll('[data-gta-tracking]');

    trackingContainers.forEach(container => {
      const trackingApp = new TrackingApp(
        container as HTMLElement,
        this.apiService,
        this.eventEmitter,
        this.config
      );

      trackingApp.init();
    });
  }

  /**
   * Initialize global event listeners
   */
  private initializeEventListeners(): void {
    // Handle browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState.bind(this));

    // Handle form submissions with tracking numbers in URL
    this.handleUrlTracking();

    // Initialize toggle functionality for shipment cards
    this.initializeToggleHandlers();

    // Global error handling
    window.addEventListener('error', this.handleGlobalError.bind(this));
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
  }

  /**
   * Initialize toggle handlers for shipment card content
   */
  private initializeToggleHandlers(): void {
    // Use event delegation for dynamically loaded content
    document.addEventListener('click', this.handleToggleClick.bind(this));
  }

  /**
   * Handle toggle button clicks
   */
  private handleToggleClick(event: Event): void {
    const target = event.target as HTMLElement;
    const button = target.closest('[data-action^="toggle-"]') as HTMLButtonElement;

    if (!button) {
      return;
    }

    const action = button.getAttribute('data-action');
    const targetSelector = button.getAttribute('data-target');

    if (
      !action ||
      !targetSelector ||
      (!action.startsWith('toggle-history') && !action.startsWith('toggle-details'))
    ) {
      return;
    }

    event.preventDefault();

    const targetElement = document.querySelector(targetSelector) as HTMLElement;
    if (!targetElement) {
      return;
    }

    // Toggle visibility
    const isHidden = targetElement.classList.contains('hidden');

    if (isHidden) {
      // Show content
      targetElement.classList.remove('hidden');
      button.setAttribute('aria-expanded', 'true');

      // Update button text
      const isHistory = action.includes('history');
      const textNodes = Array.from(button.childNodes).filter(
        node => node.nodeType === Node.TEXT_NODE
      );
      if (textNodes.length > 0) {
        if (isHistory) {
          (textNodes[0] as Text).textContent = TranslationHelper.t('label_hide_tracking_history', 'Hide Tracking History');
        } else {
          (textNodes[0] as Text).textContent = TranslationHelper.t('label_hide_shipment_details', 'Hide Shipment Details');
        }
      }
    } else {
      // Hide content
      targetElement.classList.add('hidden');
      button.setAttribute('aria-expanded', 'false');

      // Update button text
      const isHistory = action.includes('history');
      const textNodes = Array.from(button.childNodes).filter(
        node => node.nodeType === Node.TEXT_NODE
      );
      if (textNodes.length > 0) {
        if (isHistory) {
          (textNodes[0] as Text).textContent = TranslationHelper.t('label_show_tracking_history', 'Show Tracking History');
        } else {
          (textNodes[0] as Text).textContent = TranslationHelper.t('label_show_shipment_details', 'Show Shipment Details');
        }
      }
    }
  }

  /**
   * Initialize Flowbite components
   */
  private initializeFlowbite(): void {
    // Dynamically import and initialize Flowbite
    import('flowbite')
      .then(flowbite => {
        if (flowbite.initFlowbite) {
          flowbite.initFlowbite();
        }
      })
      .catch(error => {
        console.warn('[GTA GSM] Flowbite initialization failed:', error);
      });
  }

  /**
   * Initialize Swiper galleries for attachments
   */
  private initializeSwiper(): void {
    // Dynamically import Swiper and modules
    Promise.all([import('swiper'), import('swiper/modules')])
      .then(([{ Swiper }, { Navigation, Pagination }]) => {
        // Find all Swiper containers
        const swiperContainers = document.querySelectorAll('.gta-attachments-swiper');

        swiperContainers.forEach(container => {
          // Check if already initialized and destroy existing instance
          const existingInstance = (container as HTMLElement & { swiper?: SwiperInstance }).swiper;
          if (existingInstance) {
            try {
              existingInstance.destroy(true, true);
            } catch (error) {
              console.warn('[GTA GSM] Failed to destroy existing Swiper instance:', error);
            }
          }

          // Remove any existing swiper classes
          container.classList.remove('swiper-initialized');
          const wrapperElement = container.querySelector('.swiper-wrapper');
          if (wrapperElement) {
            wrapperElement.classList.remove('swiper-wrapper-initialized');
          }

          try {
            const swiperInstance = new Swiper(container as HTMLElement, {
              modules: [Navigation, Pagination],
              slidesPerView: 1,
              spaceBetween: 10,
              loop: false,
              grabCursor: true,

              // Pagination
              pagination: {
                el: container.querySelector('.swiper-pagination') as HTMLElement,
                clickable: true,
                dynamicBullets: true,
              },

              // Navigation arrows
              navigation: {
                nextEl: container.querySelector('.swiper-button-next') as HTMLElement,
                prevEl: container.querySelector('.swiper-button-prev') as HTMLElement,
              },

              // Responsive breakpoints
              breakpoints: {
                640: {
                  slidesPerView: 1,
                  spaceBetween: 15,
                },
                768: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
              },

              // Events
              on: {
                init: () => {
                  // Modal handling is now managed by ModalManager via event delegation
                  // No need to add individual click handlers
                },
              },
            });

            // Store instance reference for potential cleanup
            (container as HTMLElement & { swiper?: SwiperInstance }).swiper = swiperInstance;
          } catch (error) {
            console.error('[GTA GSM] Failed to initialize Swiper:', error);
          }
        });
      })
      .catch(error => {
        console.warn('[GTA GSM] Swiper initialization failed:', error);
      });
  }

  /**
   * Initialize modal management
   */
  private initializeModals(): void {
    // Set up global modal event delegation
    this.modalManager.setupEventDelegation(document.body);

    // Initialize common modals
    this.modalManager.reinitializeModals();
  }

  /**
   * Re-initialize Swiper galleries (called when new content is loaded)
   */
  public reinitializeSwiper(): void {
    this.initializeSwiper();
  }

  /**
   * Re-initialize modals (called when new content is loaded)
   */
  public reinitializeModals(): void {
    this.modalManager.reinitializeModals();
  }

  /**
   * Get modal manager instance
   */
  public getModalManager(): ModalManager {
    return this.modalManager;
  }

  /**
   * Handle browser back/forward navigation
   */
  private handlePopState(event: PopStateEvent): void {
    if (event.state?.trackingNumbers) {
      this.eventEmitter.emit('trackingRequested', {
        trackingNumbers: event.state.trackingNumbers,
        source: 'navigation',
      });
    }
  }

  /**
   * Handle tracking numbers passed via URL parameters
   */
  private handleUrlTracking(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingParam = urlParams.get('tracking') || urlParams.get('trackingid');

    if (trackingParam) {
      const trackingNumbers = trackingParam
        .split(',')
        .map(tn => tn.trim())
        .filter(tn => tn);
      if (trackingNumbers.length > 0) {
        // Small delay to ensure forms are initialized
        setTimeout(() => {
          this.eventEmitter.emit('trackingRequested', {
            trackingNumbers,
            source: 'url',
          });
        }, 100);
      }
    }
  }

  /**
   * Handle global JavaScript errors
   */
  private handleGlobalError(event: ErrorEvent): void {
    if (event.filename?.includes('gta-gsm')) {
      console.error('[GTA GSM] Global error:', event.error);
      this.showError('An unexpected error occurred');
    }
  }

  /**
   * Handle unhandled promise rejections
   */
  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    console.error('[GTA GSM] Unhandled promise rejection:', event.reason);
    this.showError('An unexpected error occurred');
  }

  /**
   * Show simple error message to user
   * For complex errors, rely on server-side error-message.php template
   */
  private showError(message: string): void {
    // For serious errors, trigger a page reload to get server-side error handling
    // For simple client-side notifications, use a minimal approach
    console.error('GTA GSM Tracking Error:', message);

    // Simple notification that doesn't duplicate PHP template structure
    const notification = document.createElement('div');
    notification.className =
      'gta-fixed gta-top-4 gta-right-4 gta-z-50 gta-max-w-sm gta-bg-red-100 gta-border gta-border-red-400 gta-text-red-700 gta-px-4 gta-py-3 gta-rounded';
    notification.textContent = message;

    // Safely append to body
    if (document.body) {
      document.body.appendChild(notification);
    } else {
      // Fallback if body is not ready
      document.documentElement.appendChild(notification);
    }

    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 3000);
  }

  /**
   * Get API service instance
   */
  public getApiService(): ApiService {
    return this.apiService;
  }

  /**
   * Get event emitter instance
   */
  public getEventEmitter(): EventEmitter {
    return this.eventEmitter;
  }

  /**
   * Get application configuration
   */
  public getConfig(): TrackingConfig {
    return this.config;
  }
}

/**
 * Auto-initialize when script loads
 */
(() => {
  const app = GtaGsmTrackingApp.getInstance();

  // Initialize immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => app.init());
  } else {
    app.init();
  }
})();

/**
 * Export for external access
 */
declare global {
  interface Window {
    GtaGsmTracking: typeof GtaGsmTrackingApp;
  }
}

window.GtaGsmTracking = GtaGsmTrackingApp;
